import { DashboardEventAttendees } from "@cmp-src/components/meeting/DashboardMeetingAttendees/DashboardEventAttendees";
import { ISchedule, WorkplaceRequestElement } from "@gettactic/api";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger
} from "@v2/components/ui/tabs";
import sanitizeHtml from "sanitize-html";
import { TypographyStylesProvider } from "@mantine/core";
import { Badge } from "@v2/components/ui/badge";
import { IResource, Tag } from "@gettactic/api";
import { WPRequestsPlaneTable } from "./WPRequestsPlaneTable";

export const RoomData = ({
  schedule,
  resource,
  workplaceRequests
}: {
  schedule: ISchedule;
  resource: IResource | undefined;
  workplaceRequests: WorkplaceRequestElement[];
}) => {
  return (
    <Tabs
      className="space-x-2 md:space-x-4"
      defaultValue={
        workplaceRequests.length > 0 ? "WorkplaceRequests" : "attendees"
      }
    >
      <TabsList className="px-2.5 font-bold md:px-0">
        <TabsTrigger value="attendees">
          Attendees ({schedule.slots.length})
        </TabsTrigger>
        <TabsTrigger value="amenities">Amenities</TabsTrigger>
        {workplaceRequests.length > 0 && (
          <TabsTrigger value="WorkplaceRequests">
            Workplace Requests ({workplaceRequests.length})
          </TabsTrigger>
        )}
      </TabsList>
      <TabsContent value="attendees" 
        className="ml-0 space-x-0 px-2.5 md:ml-0 md:space-x-0 md:px-0"
        >
        <DashboardEventAttendees slots={schedule.slots} />
      </TabsContent>
      <TabsContent value="amenities">
        <Amenities
          descriptions={resource?.description}
          amenities={resource?.tags}
        />
      </TabsContent>
      {workplaceRequests?.length > 0 && (
        <TabsContent
          value="WorkplaceRequests"
          className="ml-0 space-x-0 px-2.5 md:ml-0 md:space-x-0 md:px-0"
        >
          <WPRequestsPlaneTable
            schedule={schedule}
            workplaceRequests={workplaceRequests}
          />
        </TabsContent>
      )}
    </Tabs>
  );
};

const Amenities = ({
  amenities,
  descriptions
}: {
  amenities: Tag[] | undefined;
  descriptions: string | undefined | null;
}) => {
  if (!amenities || amenities.length === 0) {
    if (!descriptions) {
      return <div>No amenities listed yet</div>;
    }
    return (
      <div className="">
        <TypographyStylesProvider>
          <div
            // biome-ignore lint/security/noDangerouslySetInnerHtml: <this is intentional as description may be markdown>
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(descriptions, {
                allowedClasses: { span: ["bg-yellow-400"] }
              })
            }}
          />
        </TypographyStylesProvider>
      </div>
    );
  }
  const amenitiesByCategory = amenities.reduce(
    (acc, amenity) => {
      const category = amenity.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(amenity);
      return acc;
    },
    {} as Record<string, Tag[]>
  );

  return (
    <div className="flex flex-col gap-2 pt-6">
      {Object.entries(amenitiesByCategory).map(([type, amenities]) => (
        <div key={type} className="flex flex-row flex-wrap items-center gap-2">
          <div className="font-bold">{type}: </div>
          {amenities.map((amenity) => (
            <div key={amenity.id}>
              <Badge variant={"outline"} key={amenity.id} className="text-md">
                {amenity.name}
              </Badge>
            </div>
          ))}
        </div>
      ))}
    </div>
  );
};
